<script setup lang="ts">
import { FaTrend } from '@fantastic-admin/components'

defineOptions({
  name: 'AppStatsCards',
})

const props = withDefaults(defineProps<{
  items: AppStatsItem[]
  xlColumns?: 2 | 3 | 4
}>(), {
  xlColumns: 4,
})

type AppStatsTone = 'default' | 'primary' | 'success' | 'warning' | 'danger' | 'info'

interface AppStatsItem {
  key?: string | number
  label: string
  value: string | number
  change?: string
  changeTone?: AppStatsTone
  hint?: string
  tone?: AppStatsTone
}

const toneClassMap: Record<AppStatsTone, string> = {
  default: 'text-foreground',
  primary: 'text-primary',
  success: 'text-emerald-600',
  warning: 'text-amber-600',
  danger: 'text-red-600',
  info: 'text-sky-600',
}

const gridClassMap = {
  2: 'xl:grid-cols-2',
  3: 'xl:grid-cols-3',
  4: 'xl:grid-cols-4',
} as const

const gridClass = computed(() => {
  return ['gap-3 grid sm:grid-cols-2', gridClassMap[props.xlColumns]].join(' ')
})

function resolveKey(item: AppStatsItem, index: number) {
  return item.key ?? `${item.label}-${index}`
}

function resolveToneClass(item: AppStatsItem) {
  return toneClassMap[item.tone ?? 'default']
}

function isTrend(item: AppStatsItem) {
  return item.changeTone === 'success' || item.changeTone === 'danger'
}

function resolveTrendType(item: AppStatsItem) {
  return item.changeTone === 'danger' ? 'down' : 'up'
}
</script>

<template>
  <div :class="gridClass">
    <div
      v-for="(item, index) in props.items"
      :key="resolveKey(item, index)"
      class="border-default bg-default px-4 py-3 border rounded-3"
    >
      <div class="text-xs text-muted-foreground">
        {{ item.label }}
      </div>
      <div class="text-2xl font-semibold mt-2" :class="resolveToneClass(item)">
        {{ item.value }}
      </div>
      <FaTrend
        v-if="item.change && isTrend(item)"
        class="mt-2"
        :value="item.change"
        :type="resolveTrendType(item)"
        size="small"
        variant="soft"
      />
      <div v-else-if="item.change" class="text-xs text-muted-foreground mt-2">
        {{ item.change }}
      </div>
      <div v-if="item.hint" class="text-xs text-muted-foreground mt-2">
        {{ item.hint }}
      </div>
    </div>
  </div>
</template>
